/**
 * The click target of a `WorkshopList` row.
 *
 * A real, transparent `<button>` laid over the card rather than a `role`
 * on the card itself. The card has to stay a plain container: a caller's
 * `right` renderer can put its own controls on the row, and a control nested
 * inside something announced as a button is swallowed by that button's name
 * and can be unreachable. This also means Enter and Space work because the
 * element is a button, not because the row reimplements them.
 *
 * Anything on the row that must stay clickable sits above this overlay; the
 * card makes itself a stacking context so that layering is reliable.
 *
 * Not the stretched-link pattern `giftCards.css` uses for the same problem —
 * that anchors the control to one piece of visible text, and this row has no
 * single line that stands for the whole workshop. A venue name repeats down a
 * teacher's schedule, and the date is drawn as three stylised lines that read
 * as decoration. So the button carries its own name instead.
 */
export interface WorkshopListOpenButtonProps {
  /** Names the workshop being opened — the row shows no text inside the
   *  button itself. */
  label: string;
  /** Matches the card, so the focus ring follows its corners. */
  radius: number;
  onActivate: () => void;
}

export function WorkshopListOpenButton({ label, radius, onActivate }: WorkshopListOpenButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onActivate}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        width: '100%',
        padding: 0,
        border: 0,
        background: 'transparent',
        borderRadius: radius,
        cursor: 'pointer',
      }}
    />
  );
}
